import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import moment, { localeData } from "moment";
import { useDispatch, useSelector } from "react-redux";
import { getFirm } from "../../../Store/Admin/Firm/Firm";
import { updateAdQuatation } from "../../../Store/Admin/believe/quotation/UpdateAdQuotation";
import toast from "react-hot-toast";
import { addAdQuatation } from "../../../Store/Admin/believe/quotation/AddAdQuotation";
import {
  getAdOneQuatation,
  resetGetAdOneQuatation,
} from "../../../Store/Admin/believe/quotation/GetOneAdQuotation";
import Header from "../Include/Header";
import Sidebar from "../Include/Sidebar";
import Swal from "sweetalert2";
import { Country, State, City } from "country-state-city";
import QuotationDetails from "../../../Components/Quotation/QuotationDetails";
import ItemDetails from "../../../Components/Quotation/ItemDetails";
import TermsAndTaxSummary from "../../../Components/Quotation/TermsAndTaxSummary";

const initialState = {
  firm_id: "",
  estimate_party_data: {
    name: "",
    address: "",
    city: "Surat",
    state: "Gujarat",
    pincode: "",
  },
  estimate_details: {
    vender_code: "",
    date: moment().format("YYYY-MM-DD"),
    supply_place: "",
  },
  items: [],
  total_amount: 0,
  total_quantity: 0,
  terms_and_condition: "",
  gst_no: "",
};

const initialItemState = {
  item_name: "",
  unit_name: "",
  hsn_no: "",
  quantity: "",
  rate: "",
  amount: 0,
};

const ManageQuotation = () => {
  const { state: locationData } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get India country code
  const indiaCountry = Country.getAllCountries().find(
    (country) => country.name === "India"
  );
  const indiaCode = indiaCountry?.isoCode || "IN";

  const [stateList, setStateList] = useState([]);
  const [cityList, setCityList] = useState([]);

  const [quotationData, setQuotationData] = useState(initialState);
  const [items, setItems] = useState([{ ...initialItemState }]);
  const [error, setError] = useState({});
  const [itemErrors, setItemErrors] = useState([{}]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [taxSummary, setTaxSummary] = useState({
    subtotal: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    taxAmount: 0,
    roundOff: 0,
    total: 0,
  });
  const [canAddMoreItems, setCanAddMoreItems] = useState(false);

  const firmData = useSelector((state) => state?.getFirm?.user?.data);
  const editData = useSelector((state) => state?.getAdOneQuatation?.user?.data);
  const { loading: addLoading } = useSelector((state) => state?.addAdQuatation);
  const { loading: updateLoading } = useSelector(
    (state) => state?.updateAdQuatation
  );
  const isLoading = addLoading || updateLoading;

  useEffect(() => {
    if (indiaCode) {
      setStateList(State.getStatesOfCountry(indiaCode));
    }
  }, [indiaCode]);

  useEffect(() => {
    if (quotationData.estimate_party_data.state && indiaCode) {
      const selectedStateCode = State.getStatesOfCountry(indiaCode).find(
        (st) => st.name === quotationData.estimate_party_data.state
      )?.isoCode;

      if (selectedStateCode) {
        setCityList(City.getCitiesOfState(indiaCode, selectedStateCode));
      }
    }
  }, [quotationData.estimate_party_data.state, indiaCode]);

  useEffect(() => {
    if (firmData && firmData.length > 0) {
      setQuotationData((prev) => ({
        ...prev,
        firm_id: firmData[0]._id,
      }));
    }
  }, [firmData]);

  useEffect(() => {
    dispatch(getFirm());
  }, []);

  useEffect(() => {
    if (locationData?._id) {
      dispatch(getAdOneQuatation(locationData._id));
    } else {
      dispatch(resetGetAdOneQuatation());
      setQuotationData(initialState);
      setItems([{ ...initialItemState }]);
      setItemErrors([{}]);
    }
  }, [dispatch, locationData]);

  useEffect(() => {
    return () => {
      dispatch(resetGetAdOneQuatation());
    };
  }, [dispatch]);

  useEffect(() => {
    if (editData) {
      setQuotationData({
        firm_id: editData?.firm_id,
        estimate_party_data: {
          name: editData?.estimate_party_data?.name || "",
          address: editData?.estimate_party_data?.address || "",
          city: editData?.estimate_party_data?.city || "Surat",
          state: editData?.estimate_party_data?.state || "Gujarat",
          pincode: editData?.estimate_party_data?.pincode || "",
          gst_no: editData?.estimate_party_data.gst_no || "",
        },
        estimate_details: {
          vender_code: editData?.estimate_details?.vender_code || "",
          date: editData?.estimate_details?.date
            ? moment(editData?.estimate_details?.date).format("YYYY-MM-DD")
            : moment().format("YYYY-MM-DD"),
          supply_place: editData?.estimate_details?.supply_place || "",
        },
        total_amount: editData?.total_amount || 0,
        total_quantity: editData?.total_quantity || 0,
        terms_and_condition: editData?.terms_and_condition || "",
      });

      if (editData?.items && editData.items.length > 0) {
        const clonedItems = editData.items.map((item) => ({
          ...item,
          unit_name: item.unit_name || "",
        }));

        setItems(clonedItems);
        setItemErrors(editData.items.map(() => ({})));
      }
    } else {
      setQuotationData(initialState);
      setItems([{ ...initialItemState }]);
      setItemErrors([{}]);
    }
  }, [editData]);

  // Check if current item row is valid for adding more
  useEffect(() => {
    const currentItem = items[items.length - 1];
    const isValid =
      currentItem?.item_name?.trim() &&
      currentItem?.quantity &&
      parseFloat(currentItem.quantity) > 0 &&
      currentItem?.rate &&
      parseFloat(currentItem.rate) > 0;
    setCanAddMoreItems(isValid);
  }, [items]);

  // Calculate tax based on firm and party state
  const calculateTaxType = () => {
    const firmState = firmData
      ?.find((f) => f._id === quotationData.firm_id)
      ?.state?.toLowerCase()
      ?.trim();
    const partyState = quotationData.estimate_party_data.state
      ?.toLowerCase()
      ?.trim();

    // If states match, use CGST+SGST, otherwise use IGST
    return firmState && partyState && firmState === partyState
      ? "cgst_sgst"
      : "igst";
  };

  const subtotal = items.reduce(
    (sum, item) => sum + (parseFloat(item.amount) || 0),
    0
  );
  const totalQty = items.reduce(
    (sum, item) => sum + (parseFloat(item.quantity) || 0),
    0
  );

  const totalRate = items.reduce(
    (sum, item) => sum + (parseFloat(item.rate) || 0),
    0
  );

  // Calculate all totals and taxes
  useEffect(() => {
    const taxType = calculateTaxType();
    const taxRate = 0.18; // 18% tax
    const taxAmount = subtotal * taxRate;

    let cgst = 0,
      sgst = 0,
      igst = 0;

    if (taxType === "cgst_sgst") {
      cgst = taxAmount / 2;
      sgst = taxAmount / 2;
    } else {
      igst = taxAmount;
    }

    const totalBeforeRound = subtotal + taxAmount;
    const roundOff = Math.round(totalBeforeRound) - totalBeforeRound;
    const total = Math.round(totalBeforeRound);

    setTaxSummary({
      subtotal,
      cgst,
      sgst,
      igst,
      taxAmount,
      roundOff,
      total,
    });

    setQuotationData((prev) => ({
      ...prev,
      total_amount: total,
      total_quantity: totalQty,
    }));
  }, [
    items,
    quotationData.firm_id,
    quotationData.estimate_party_data.state,
    firmData,
  ]);

  // Validate individual item
  const validateItem = (index) => {
    const item = items[index];
    const errors = {};

    if (!item.item_name || item.item_name.trim() === "") {
      errors.item_name = "Item name is required";
    }
    if (!item.quantity || parseFloat(item.quantity) <= 0) {
      errors.quantity = "Valid quantity is required";
    }
    if (!item.rate || parseFloat(item.rate) <= 0) {
      errors.rate = "Valid rate is required";
    }

    const updatedItemErrors = [...itemErrors];
    updatedItemErrors[index] = errors;
    setItemErrors(updatedItemErrors);

    return Object.keys(errors).length === 0;
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;

    if (field === "quantity" || field === "rate") {
      const quantity = parseFloat(updatedItems[index].quantity) || 0;
      const rate = parseFloat(updatedItems[index].rate) || 0;
      updatedItems[index].amount = (quantity * rate).toFixed(2);
    }

    setItems(updatedItems);

    const updatedItemErrors = [...itemErrors];
    if (updatedItemErrors[index] && updatedItemErrors[index][field]) {
      delete updatedItemErrors[index][field];
      setItemErrors(updatedItemErrors);
    }
  };

  const handleItemBlur = (index, field) => {
    if (field === "item_name" || field === "quantity" || field === "rate") {
      validateItem(index);
    }
  };

  const handleAddItem = () => {
    const lastItem = items[items.length - 1];

    if (!lastItem.item_name?.trim() || !lastItem.quantity || !lastItem.rate) {
      toast.error(
        "Please complete current item details before adding new item"
      );
      return;
    }

    if (parseFloat(lastItem.quantity) <= 0 || parseFloat(lastItem.rate) <= 0) {
      toast.error("Please enter valid quantity and rate for current item");
      return;
    }

    setItems([...items, { ...initialItemState }]);
    setItemErrors([...itemErrors, {}]);
    toast.success("New item row added");
  };

  // Remove item row with SweetAlert confirmation
  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      toast.error("At least one item is required");
      return;
    }

    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to delete this item?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        const updatedItems = items.filter((_, i) => i !== index);
        const updatedItemErrors = itemErrors.filter((_, i) => i !== index);
        setItems(updatedItems);
        setItemErrors(updatedItemErrors);

        Swal.fire({
          title: "Deleted!",
          text: "Item has been deleted.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    });
  };

  // Handle form field changes
  const handleChange = (section, field, value) => {
    if (section === "estimate_party_data" || section === "estimate_details") {
      setQuotationData((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value,
        },
      }));
    } else {
      setQuotationData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }

    // Clear error for this field
    if (error[field] || error[`party_${field}`]) {
      setError((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        delete newErrors[`party_${field}`];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!quotationData.firm_id) errors.firm_id = "Firm is required";
    if (!quotationData.estimate_party_data.name)
      errors.party_name = "Party name is required";
    if (!quotationData.estimate_party_data.address)
      errors.party_address = "Party address is required";
    if (!quotationData.estimate_party_data.state)
      errors.party_state = "Party state is required";
    if (!quotationData.estimate_details.date) errors.date = "Date is required";

    // Validate all items
    let hasItemErrors = false;
    const newItemErrors = items.map((item, index) => {
      const itemError = {};
      if (!item.item_name || item.item_name.trim() === "") {
        itemError.item_name = "Required";
        hasItemErrors = true;
      }
      if (!item.quantity || parseFloat(item.quantity) <= 0) {
        itemError.quantity = "Required";
        hasItemErrors = true;
      }
      if (!item.rate || parseFloat(item.rate) <= 0) {
        itemError.rate = "Required";
        hasItemErrors = true;
      }
      return itemError;
    });

    if (hasItemErrors) {
      errors.items = "Please fill all required item fields";
      setItemErrors(newItemErrors);
    }

    setError(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      toast.error("Please fill all required fields");
      return;
    }

    const payload = {
      firm_id: quotationData.firm_id,
      estimate_party_data: quotationData.estimate_party_data,
      estimate_details: quotationData.estimate_details,
      items: items,
      total_amount: quotationData.total_amount,
      total_quantity: quotationData.total_quantity,
      terms_and_condition: quotationData.terms_and_condition,
    };

    if (locationData?._id) {
      dispatch(
        updateAdQuatation({ id: locationData?._id, payload: payload })
      ).then((response) => {
        const { success, message } = response?.payload;
        if (success) {
          toast.success(message);
          navigate("/admin/quotation-management");
        }
      });
    } else {
      dispatch(addAdQuatation(payload)).then((response) => {
        const { success, message } = response?.payload;
        if (success) {
          toast.success(message);
          navigate("/admin/quotation-management");
        }
      });
    }
  };

  const handleReset = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "All form data will be cleared!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, reset it!",
      cancelButtonText: "Cancel",
    }).then((result) => {
      if (result.isConfirmed) {
        setQuotationData(initialState);
        setItems([{ ...initialItemState }]);
        setError({});
        setItemErrors([{}]);
        toast.success("Form has been reset");
      }
    });
  };

  const handleOpen = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const taxType = calculateTaxType();

  return (
    <div className={`main-wrapper ${isSidebarOpen ? "slide-nav" : ""}`}>
      <Header handleOpen={handleOpen} />
      <Sidebar />

      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <ul className="breadcrumb">
                  <li className="breadcrumb-item">
                    <Link to="/admin/dashboard">Dashboard</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right"></i>
                  </li>
                  <li className="breadcrumb-item">
                    <Link to="/admin/quotation-management">Quotation List</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <i className="feather-chevron-right"></i>
                  </li>
                  <li className="breadcrumb-item active">
                    {locationData?._id ? "Edit" : "Add"} Quotation
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">
                  {/* Header Section */}
                  <div className="row">
                    <div className="col-12">
                      <div className="form-heading mb-4">
                        <h4 className="mb-0">
                          {locationData?._id ? "Edit" : "Add"} Quotation Details
                        </h4>
                      </div>
                    </div>
                  </div>

                  {/* Firm Selection & Date */}
                  <QuotationDetails
                    quotationData={quotationData}
                    error={error}
                    firmData={firmData}
                    handleChange={handleChange}
                    indiaCode={indiaCode}
                    cityList={cityList}
                    stateList={stateList}
                  />

                  {/* Items Section */}
                  <ItemDetails
                    items={items}
                    itemErrors={itemErrors}
                    handleItemChange={handleItemChange}
                    handleItemBlur={handleItemBlur}
                    handleAddItem={handleAddItem}
                    handleRemoveItem={handleRemoveItem}
                    canAddMoreItems={canAddMoreItems}
                    isLoading={isLoading}
                    totalQty={totalQty}
                    totalRate={totalRate}
                    subtotal={subtotal}
                    error={error}
                  />

                  {/* Tax Summary Section */}
                  <TermsAndTaxSummary
                    quotationData={quotationData}
                    handleChange={handleChange}
                    taxSummary={taxSummary}
                    taxType={taxType}
                  />

                  {/* Submit Buttons */}
                  <div className="row mt-4">
                    <div className="col-12">
                      <div className="doctor-submit text-end">
                        <button
                          type="button"
                          className="btn btn-primary submit-form me-2"
                          onClick={handleSubmit}
                          disabled={isLoading}
                        >
                          {isLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Processing...
                            </>
                          ) : (
                            <>{locationData?._id ? "Update" : "Submit"}</>
                          )}
                        </button>
                        <button
                          type="button"
                          className="btn btn-primary cancel-form me-2"
                          onClick={handleReset}
                          disabled={isLoading}
                        >
                          Reset
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageQuotation;
